import { Injectable } from '@angular/core';
import { getPageClassBySegment } from '../app/app-page-helpers';
import { parseUrlToPages, pagesToUrl, type PageWithParams } from '../utils/url-parser';
import { filterUrlParameters, NavStackLogger } from '../utils/nav-stack-converter';
import { describeErrorForLog } from '@cf-shared/error-describe';

export interface PageStackEntry {
  pageName: string; // Now stores page segment (e.g., 'settings') instead of class name (e.g., 'SettingsPage')
  params: any;
}

@Injectable()
export class NavigationBreadcrumbService {
  private logging: NavStackLogger;

  constructor() { }

  /**
   * Set the logger for tracking abnormal navigation errors.
   * Called during app initialization to avoid circular dependency.
   */
  setLogger(logger: NavStackLogger): void {
    this.logging = logger;
  }

  /**
   * Encode a page stack into a URL-safe path string.
   * Format: page1:param1~value1:param2~value2/page2:param1~value/page3
   *
   * @param pageStack Array of page entries to encode
   * @returns Encoded path string
   */
  encodeBreadcrumbs(pageStack: PageStackEntry[]): string {
    if (!pageStack || pageStack.length === 0) {
      return '';
    }

    // Convert PageStackEntry array to PageWithParams array for url-parser
    const pages: PageWithParams[] = pageStack.map(entry => ({
      segment: entry.pageName,
      params: filterUrlParameters(entry.params, this.logging)
    }));

    // Use url-parser to generate URL, then remove leading slash
    const url = pagesToUrl(pages);
    return url.substring(1);
  }

  /**
   * Decode a path string back into a page stack.
   * Format: page1:param1~value1:param2~value2/page2:param1~value/page3
   *
   * @param pathString Encoded path string
   * @returns Array of page stack entries
   */
  decodeBreadcrumbs(pathString: string): PageStackEntry[] {
    const { pageStack, dropped } = this.readTrailEntries(pathString);

    // Report every entry that had to be dropped. Losing one costs the user a page they
    // could have gone Back to, and the trail is normally one the app wrote itself, so a
    // name it cannot read back is a defect in the writer (df-5f621deef600). A stale or
    // hand-edited link reaches here too and is reported the same way — the app has no way
    // to tell the two apart. No user-facing message: navigation continues through the
    // fallback, so there is nothing for the user to do about it.
    for (const segment of dropped) {
      this.reportUnreadableEntry(segment);
    }

    return pageStack;
  }

  /**
   * Read a trail string into the pages it names, reporting nothing.
   *
   * Separate from `decodeBreadcrumbs` so that `validateBreadcrumbs` can ask the same
   * question without filing a report: its callers decode the very same string again the
   * moment it answers true, and that decode is what reports. Reporting in both would
   * file every unreadable entry twice for one navigation.
   *
   * @param pathString Encoded path string
   * @returns The pages it named, and the page segments that could not be read
   */
  private readTrailEntries(pathString: string): { pageStack: PageStackEntry[]; dropped: string[] } {
    if (!pathString || pathString.trim() === '') {
      return { pageStack: [], dropped: [] };
    }

    // Use url-parser to parse the path (add leading slash for parser)
    const pages = parseUrlToPages('/' + pathString);

    const pageStack: PageStackEntry[] = [];
    const dropped: string[] = [];

    for (const page of pages) {
      // Validate that this is a real page
      if (!this.isValidPageName(page.segment)) {
        dropped.push(page.segment);
        continue;
      }

      pageStack.push({
        pageName: page.segment,
        params: page.params
      });
    }

    return { pageStack, dropped };
  }

  /**
   * Say that a trail entry could not be read back.
   *
   * @param segment The page segment that no page definition matches
   */
  private reportUnreadableEntry(segment: string): void {
    this.reportTrailProblem(`NavigationTrail: Invalid page name in path: ${segment}`);
  }

  /**
   * Say that something went wrong with the back-trail.
   *
   * Both lines are deliberate. `logging` is undefined until `setLogger` runs during app
   * init, so with only the reported line a drop in that window would produce no output at
   * all — the same silent absorption this reporting was added to end. The console line
   * always writes; the report reaches telemetry once a logger exists.
   * `post-browser-nav-callbacks.ts` keeps the same pair for the same reason.
   *
   * @param message What went wrong, as the line to log and report
   */
  private reportTrailProblem(message: string): void {
    console.warn(message);
    this.logging?.trackAbnormalError(message);
  }

  /**
   * Validate that a breadcrumbs contains only valid page names and structure.
   *
   * @param breadcrumbs Trail string to validate
   * @returns True if breadcrumbs is valid
   */
  validateBreadcrumbs(breadcrumbs: string): boolean {
    if (!breadcrumbs || breadcrumbs.trim() === '') {
      return true; // Empty breadcrumbs is valid
    }

    try {
      // read it without reporting: when this answers true the caller decodes the same
      // string again straight away, and that decode is what reports each dropped entry
      const { pageStack, dropped } = this.readTrailEntries(breadcrumbs);
      if (pageStack.length === 0) {
        // nothing usable, so no second decode follows and this is the only chance to say
        // the whole trail was thrown away — which is what costs the user their back-trail
        for (const segment of dropped) {
          this.reportUnreadableEntry(segment);
        }
        return false;
      }
      return true; // If we could decode anything, it's valid
    } catch (error) {
      // reading a trail answers "nothing usable" rather than throwing, so a throw here is
      // a bug that must reach a debugger (#5191)
      this.reportTrailProblem(`NavigationTrail: Trail validation failed: ${describeErrorForLog(error)}`);
      return false;
    }
  }

  /**
   * Check if a page segment is valid (exists in the app's page registry).
   *
   * @param pageName Page segment to check (despite parameter name, this is now a segment)
   * @returns True if page exists
   */
  private isValidPageName(pageName: string): boolean {
    return getPageClassBySegment(pageName) !== undefined;
  }
}